import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { WorkOrderFindingsFields } from './WorkOrderFindingsFields';
import { toast } from 'sonner';
import { WorkPartsFields } from './WorkPartsFields';
import { serializedPayload } from '../lib/serializedWork';
import InstallationPointMultiSelect from './InstallationPointMultiSelect';

/**
 * Prompt shown when the user closes a work order. All three fields are
 * optional — some WOs are trivial ("adjusted set-screw, done") and don't
 * warrant paperwork.
 *
 * Fields:
 *   • Hours Spent (number)
 *   • Completed On (datetime-local, defaults to now in the user's local TZ)
 *   • Parts Consumed (PartsPicker → inventory auto-deducts on submit)
 *
 * On submit, PUTs `status: "completed"` plus the completion payload. The
 * backend handles inventory deduction and audit trail (see routes/work_orders.py).
 */

const _localDatetimeNow = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const CompleteWorkOrderDialog = ({ open, onClose, onSuccess, workOrder }) => {
  const [hours, setHours] = useState('');
  const [completedAtLocal, setCompletedAtLocal] = useState('');
  const [selectedParts, setSelectedParts] = useState([]);
  const [serializedParts, setSerializedParts] = useState([]);
  const [installationPoints, setInstallationPoints] = useState([]);
  const [selectedInstallationPointIds, setSelectedInstallationPointIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  const [findings, setFindings] = useState([]);
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());

  useEffect(() => {
    if (!open) return;
    setHours('');
    setCompletionNotes('');
    setFindings([]);
    setRequestId(crypto.randomUUID());
    setCompletedAtLocal(_localDatetimeNow());
    setSelectedParts([]);
    setSerializedParts([]);
    // Pre-check any installation points already set on the WO (e.g. resumed
    // after cancel or edited before completion).
    setSelectedInstallationPointIds(workOrder?.installation_point_ids || []);
    // Load the machinery's installation points so techs can pick what got
    // touched during this work order.
    if (workOrder?.machinery_id) {
      axios
        .get(`${API}/machinery/${workOrder.machinery_id}/installation-points`)
        .then((r) => setInstallationPoints(r.data || []))
        .catch(() => setInstallationPoints([]));
    } else {
      setInstallationPoints([]);
    }
  }, [open, workOrder]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Convert the datetime-local (interpreted in the user's local TZ) to a
      // UTC ISO string. The datetime-local input value never carries a TZ
      // suffix, so `new Date(v)` correctly interprets it as local.
      let completedAtISO = null;
      if (completedAtLocal) {
        completedAtISO = new Date(completedAtLocal).toISOString();
      }

      const partsUsedString = selectedParts
        .map((p) => (p.qty > 1 ? `${p.part_number} x${p.qty}` : p.part_number))
        .join(', ');

      const confirmedDeductions = selectedParts
        .filter((p) => !p.isNew)
        .map((p) => ({ part_number: p.part_number, qty: p.qty }));

      const newInventoryItems = selectedParts
        .filter((p) => p.isNew)
        .map((p) => ({ part_number: p.part_number, name: p.name }));

      const payload = {
        completion_request_id: requestId,
        completion_notes: completionNotes || null,
        findings,
        serialized_actions: serializedPayload(serializedParts),
        status: 'completed',
        completed_at: completedAtISO,
        hours_spent: hours === '' ? null : parseFloat(hours),
        parts_used: partsUsedString || null,
        installation_point_ids: selectedInstallationPointIds,
        confirmed_deductions: confirmedDeductions.length ? confirmedDeductions : null,
        new_inventory_items: newInventoryItems.length ? newInventoryItems : null,
      };

      const res = await axios.put(`${API}/work-orders/${workOrder.id}`, payload);

      const totalUnits = confirmedDeductions.reduce((s, d) => s + (d.qty || 0), 0);
      const newCount = newInventoryItems.length;
      const bits = [`Work order ${res.data.wo_number ? `#${res.data.wo_number} ` : ''}completed`];
      if (totalUnits) bits.push(`${totalUnits} unit${totalUnits === 1 ? '' : 's'} deducted`);
      if (newCount) bits.push(`${newCount} new part${newCount === 1 ? '' : 's'} added (qty 0)`);
      const outstanding = findings.filter((f) => f.disposition === 'follow_up').length;
      if (outstanding) bits.push(`${outstanding} follow-up${outstanding === 1 ? '' : 's'} tracked separately`);
      toast.success(bits.join(' · '));

      onSuccess?.();
      onClose();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Check the completion fields and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!workOrder) return null;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !submitting) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="complete-work-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {workOrder.wo_number && (
              <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                #{workOrder.wo_number}
              </span>
            )}
            Complete Work Order
          </DialogTitle>
          <DialogDescription>
            {workOrder.title}
            <span className="block text-xs text-slate-500 mt-1">
              All fields optional. Skip anything that doesn&apos;t apply.
            </span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cwo-hours">Hours Spent</Label>
              <Input
                id="cwo-hours"
                data-testid="complete-wo-hours-input"
                type="number"
                inputMode="decimal"
                step="0.25"
                min="0"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                placeholder="e.g., 1.5"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cwo-completed-at">Completed On</Label>
              <Input
                id="cwo-completed-at"
                data-testid="complete-wo-completed-at-input"
                type="datetime-local"
                step="1"
                value={completedAtLocal}
                onChange={(e) => setCompletedAtLocal(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="complete-wo-notes">Completion notes (optional)</Label>
            <Textarea id="complete-wo-notes" data-testid="complete-wo-notes" value={completionNotes}
              disabled={submitting} maxLength={20000} rows={3} onChange={(e) => setCompletionNotes(e.target.value)} />
          </div>
          <WorkOrderFindingsFields value={findings} onChange={setFindings} disabled={submitting} />

          <div className="space-y-2">
            <WorkPartsFields
              parts={selectedParts}
              onPartsChange={setSelectedParts}
              serialized={serializedParts}
              onSerializedChange={setSerializedParts}
              machineryId={workOrder.machinery_id}
              disabled={submitting}
            />
          </div>

          {installationPoints.length > 0 && (
            <div className="space-y-2">
              <Label>Installation Points Touched (Optional)</Label>
              <InstallationPointMultiSelect
                points={installationPoints}
                value={selectedInstallationPointIds}
                onChange={setSelectedInstallationPointIds}
                testIdPrefix="cwo-ip"
                disabled={submitting}
              />
              <p className="text-xs text-slate-500">
                Pick every point this WO covered. Serialized parts installed
                there at completion time will attribute this WO to themselves.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting} data-testid="complete-work-order-cancel">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
              data-testid="complete-wo-submit-btn"
            >
              {submitting ? 'Completing...' : 'Mark Completed'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CompleteWorkOrderDialog;
